import { weekDays, getWeekNumber, copyDate, dateToString, months } from '../../../utilities/calendar-utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { ChangeCalendarView } from './Calendar';
import { Trans } from 'react-i18next';
import { createRef } from 'react';
import CoreComponent from 'utilities/component-utilities/CoreComponent';

// #region model definition
interface CalendarRangeDayProps {
    name: string,
    showWeekNumber?: boolean,
    isDisabledDate: (date: Date) => boolean,
    viewYear: number,
    viewMonth: number,
    onChange: (date: Date[], e: React.MouseEvent) => void,
    value: Date[],
    changeView: (view: ChangeCalendarView, e?: React.MouseEvent | WheelEvent) => void
    returnDates?: 'all' | 'edges', // default: 'all'
    calculateAsNights?: boolean
}
interface CalendarRangeDayState {
    firstDateSelected?: Date,
    hoverDays: Date[]
}
// #endregion model definition
class CalendarRangeDay extends CoreComponent<CalendarRangeDayProps, CalendarRangeDayState> {
    readonly today: Date;
    readonly bodyElement;
    readonly calendarContentRef: React.RefObject<HTMLDivElement | null>;
    private onWheelEvent: any;

    constructor(props: CalendarRangeDayProps) {
        super(props);
        this.today = new Date();
        this.state = {
            firstDateSelected: undefined,
            hoverDays: []
        };

        this.calendarContentRef = createRef<HTMLDivElement | null>();
        this.bodyElement = createRef<HTMLTableSectionElement>();
    }

    // #region init
    componentDidMount() {
        document.body.addEventListener('click', this.onFocus);
    }

    componentWillUnmount() {
        this.mouseLeave();
        document.body.removeEventListener('click', this.onFocus);
        // if (this.inputElement?.current) this.inputElement.current.removeEventListener('keydown', this.onClick);
    }

    onFocus = (e: MouseEvent | KeyboardEvent) => {
        if (this.bodyElement && this.bodyElement.current?.contains(e.target as Node)) {
            return;
        }

        this.setState({
            firstDateSelected: undefined,
            hoverDays: []
        });
    }
    // #endregion init

    private mouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        this.onWheelEvent = this.calendarContentRef.current?.addEventListener('wheel', e => {
            e.preventDefault();
            e.stopPropagation();
            if (e.deltaY > 0) {
                this.moveMonth(1, e);
            } else if (e.deltaY < 0) {
                this.moveMonth(-1, e);
            }
        });
    }

    private mouseLeave = () => {
        this.calendarContentRef.current?.removeEventListener('wheel', this.onWheelEvent);
    }

    // #region logic
    private goToToday = (e: React.MouseEvent) => {
        this.props.changeView({
            month: this.today.getMonth(),
            year: this.today.getFullYear(),
            view: 'date'
        }, e);
    }

    private moveMonth = (moveFor: number, e: React.MouseEvent | WheelEvent) => {
        let month = this.props.viewMonth + moveFor;
        let year = this.props.viewYear + 0;
        if (month === -1) {
            month = 11;
            year--;
        }
        if (month === 12) {
            month = 0;
            year++;
        }

        this.props.changeView({
            month: month,
            year: year
        }, e);
    }

    private onDateSelected = (day: Date, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.state.firstDateSelected) {
            if (this.state.firstDateSelected <= day) {
                const selectedDays = [this.state.firstDateSelected];
                if (this.props.returnDates == 'edges') {
                    selectedDays.push(day);
                } else {
                    while (selectedDays[selectedDays.length - 1] < day) {
                        let newDate = copyDate(selectedDays[selectedDays.length - 1]);
                        newDate.setDate(newDate.getDate() + 1);
                        selectedDays.push(newDate);
                    }
                }

                this.props.onChange(selectedDays, e);
                this.setState({
                    firstDateSelected: undefined,
                    hoverDays: []
                });

                return;
            }
        }

        this.setState({
            firstDateSelected: day
        });
    }

    private onMouseEnter = (day: Date) => {
        if (this.state.firstDateSelected && day > this.state.firstDateSelected) {
            const selectedDays = [this.state.firstDateSelected];
            while (selectedDays[selectedDays.length - 1] < day) {
                let newDate = copyDate(selectedDays[selectedDays.length - 1]);
                newDate.setDate(newDate.getDate() + 1);
                selectedDays.push(newDate);
            }

            this.setState({
                hoverDays: [...selectedDays]
            });
        }
    }

    private onMouseLeave = (day: Date) => {
        this.setState({
            hoverDays: []
        });
    }
    // #endregion logic

    // #region render


    private get firstDateOfThisMonth() {
        return new Date(this.props.viewYear, this.props.viewMonth, 1);
    }

    private get lastDayOfThisMonth() {
        return new Date(this.props.viewYear, this.props.viewMonth + 1, 0);
    }

    private renderWeekNumberHeader = () => {
        if (this.props.showWeekNumber) return <th key="week-number-header" className='week-number'>#</th>;
        else return <></>;
    }

    private renderDayOnCalendar = (date: Date) => {
        const day = copyDate(date);
        const dayString = dateToString(day);
        let className = weekDays[date.getDay()];
        if (dayString < dateToString(this.firstDateOfThisMonth) || dayString > dateToString(this.lastDayOfThisMonth)) {
            className += " not-in-month";
        }

        if (dayString === dateToString(this.today)) {
            className += ' today';
        }

        if (!this.state.firstDateSelected && this.props.value && this.props.value.some(d => dayString === dateToString(d))) {
            className += ' selected';
        }

        if (this.state.firstDateSelected && dayString === dateToString(this.state.firstDateSelected)) {
            className += ' selected-first';
        }

        if(this.state.hoverDays.length && this.state.hoverDays.some(d => dayString === dateToString(d))) {
            className += ' hovered';
        }

        if (this.props.isDisabledDate(date)) className += ' disabled';

        return (
            <td key={this.props.name + dayString}>
                <button
                    className={className}
                    type="button"
                    name={`${this.props.name}-calendar-day-${dayString}`}
                    onMouseEnter={e => this.onMouseEnter(day)}
                    onMouseLeave={e => this.onMouseLeave(day)}
                    onClick={e => this.onDateSelected(day, e)}
                >
                    <span className={`${day.getDate() < 10 ? 'single-digits' : ''}`}>{day.getDate()}</span>
                </button>
            </td>
        );
    }

    private renderDateBody = () => {
            let dayOfWeek = this.firstDateOfThisMonth.getDay();
            dayOfWeek = (dayOfWeek === 0 ? 7 : dayOfWeek) - 2;
            let firstDayOfCalendar = new Date(new Date(this.firstDateOfThisMonth.setDate(dayOfWeek * -1)));
            const calendarBody: React.ReactNode[] = [];
    
            for (let calendarRowIndex = 0; calendarRowIndex < 6; calendarRowIndex++) {
                const calendarColumns = [];
                if (this.props.showWeekNumber) {
                    const weekNumber = getWeekNumber(firstDayOfCalendar);
                    calendarColumns.push(
                        <td key={`week-number-header-${weekNumber}`}>
                            <span className='week-number'>{weekNumber}</span>
                        </td>
                    );
                }
                for (let calendarColumnIndex = 0; calendarColumnIndex < 7; calendarColumnIndex++) {
                    calendarColumns.push(this.renderDayOnCalendar(firstDayOfCalendar));
                    firstDayOfCalendar.setDate(firstDayOfCalendar.getDate() + 1);
                }
    
                calendarBody.push(
                    <tr key={`${this.props.name}-calendar-table-row-${calendarRowIndex}`}>
                        {calendarColumns}
                    </tr>
                );
            }
    
            return calendarBody;
    }

    private renderFooter = () => {
        if(this.state.hoverDays.length) {
            let length = this.state.hoverDays.length;
            if(this.props.calculateAsNights) length = length > 0 ? length - 1 : length;
            return <div><Trans i18nKey="calendar.hover-days" />: {length}</div>;
        }

        return <></>;
    }

    render() {
        return (
            <>
                <header className="calendar-header"><div className='flex gap-4 text-4xl float-right'>
                    <button aria-label='up' type='button' onClick={e => this.moveMonth(-1, e)} ><FontAwesomeIcon icon={faCaretUp} /></button>
                    <button aria-label='down' type='button' onClick={e => this.moveMonth(1, e)} ><FontAwesomeIcon icon={faCaretDown} /></button>
                </div>
                    <div className='pt-2 pb-2'>
                        <button type='button' onClick={e => this.props.changeView('month', e)} >
                            <Trans i18nKey={`calendar.months.${months[this.props.viewMonth]}.name`} />
                            &nbsp;
                            {this.props.viewYear}
                        </button>
                        &nbsp;
                        &nbsp;
                        &nbsp;
                        &nbsp;
                        <button type='button' onClick={e => this.goToToday(e)} >{this.t('calendar.today')}</button>
                    </div>
                </header>
                <div ref={this.calendarContentRef} className="calendar-content" onMouseEnter={this.mouseEnter} onMouseLeave={this.mouseLeave}>
                    <table className="date-view">
                        <thead>
                            <tr key='calendar-table-header'>
                                {this.renderWeekNumberHeader()}
                                <th key="monday">{this.t('calendar.weeks.monday.shot')}</th>
                                <th key="tuesday">{this.t('calendar.weeks.tuesday.shot')}</th>
                                <th key="wednesday">{this.t('calendar.weeks.wednesday.shot')}</th>
                                <th key="thursday">{this.t('calendar.weeks.thursday.shot')}</th>
                                <th key="friday">{this.t('calendar.weeks.friday.shot')}</th>
                                <th key="saturday">{this.t('calendar.weeks.saturday.shot')}</th>
                                <th key="sunday">{this.t('calendar.weeks.sunday.shot')}</th>
                            </tr>
                        </thead>
                        <tbody ref={this.bodyElement}>
                            {this.renderDateBody()}
                        </tbody>
                    </table>
                    {this.renderFooter()}
                </div>
            </>
        );
    }
    // #endregion render
}
export default CalendarRangeDay;
